/**
 * @file HaulagePoDetailsModal.js
 * @version 2.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-19
 * @modified 2026-10-30
 * @description A PO-H's collection address and notes (Cam, 19 Oct 2026: "edit
 *              the collection address on a PO-H please and add notes to it for
 *              internal use only and also notes for haulier to see"). One block
 *              per collection point — the supplier (or our stock) its lines are
 *              collected from — with what the order says beside what is typed;
 *              notes for the haulier (printed on the PO-H) and internal notes
 *              (never printed or sent). GET / PATCH
 *              /api/purchase-orders/:id/haulage-details; rules
 *              utils/haulagePoDetails.js. onSaved(answer) hands back the save,
 *              whose `revised` names a sent PO-H the haulier should see again.
 *
 *              2.0.0 (2026-10-19, Cam: "allow the edit button to work in PO-H
 *              modal and PO search table … even if only the delivery address
 *              and notes can be edited. Also, can the customer field be a
 *              searchable dropdown, and auto fill the delivery address stored
 *              with the selected customer? Style the dropdown like the
 *              customer picker in create customer order"): this is now the
 *              PO-H's EDIT window (Search POs' Edit and the PO window's Edit).
 *              Deliver to — Create Customer Order's customer picker (account
 *              badge, "Name (address line 1)", search by # or name) over every
 *              active contact — fills the address from that contact's delivery
 *              address (else its invoice address); the address and the
 *              delivery instructions stay editable. They are the PO-H's OWN:
 *              the order is unchanged, "Back to the order's" puts its back, and
 *              lines, haulier, date and service still follow the order. Greyed
 *              with the reason (never hidden) before the migration and on a
 *              billed PO-H; after delivery it is a record (no revised PO-H).
 *
 *              2.1.0 (2026-10-19, Cam: "make the collection field a dropdown of
 *              all contacts (suppliers and customers) — the delivery one
 *              doesn't need to be there as this can be edited in the customer
 *              order that the PO-H comes from"): Collect from is that picker,
 *              over every active contact of any type; choosing one fills the
 *              collection address from it (editable). The Delivery section is
 *              gone — the PO-H delivers where its order (or self-billed PO)
 *              says. The server's delivery override stays, unused here.
 *              2.1.1: each option reads as Create Customer Order's — the
 *              account badge, the name and the first line of the contact's
 *              DELIVERY address (else its address, else its invoice address).
 *              2.2.0 (2026-10-29, Cam): a PO-H raised for a stock or
 *              self-billed PO (no customer order) says "the PO" where an
 *              order's says "the order" — "Use the PO's", "The PO says" — and
 *              its save may answer `send_offer` (never sent: the caller offers
 *              to send it) beside `revised`.
 */

import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import Textarea from './Textarea';
import SearchableDropdown from './SearchableDropdown';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import { API_URL } from '../config';
import { NOTE_MAX, ADDRESS_MAX } from '../utils/haulagePoDetails';
import { formatAddress, formatAddressFromFlat, addressLine1 } from '../utils/formatAddress';
import './PurchaseOrderModal.css';

const trimmed = (v) => String(v ?? '').replace(/\r\n?/g, '\n').trim();

/**
 * A contact's site address for the PO-H: its delivery address, else its
 * invoice address, else the old single field — one line per row, no name (the
 * PO-H names the contact above the address).
 */
export function contactDeliveryAddress(c) {
  if (!c) return '';
  const text = formatAddressFromFlat(c, 'delivery_', { separator: '\n' })
    || formatAddressFromFlat(c, 'invoice_', { separator: '\n' })
    || (c.address ? formatAddress(c.address, { separator: '\n' }) : '');
  return String(text || '').split('\n').map(s => s.trim()).filter(Boolean)
    .filter(l => l.toLowerCase() !== String(c.company_name || '').trim().toLowerCase())
    .join('\n');
}

/**
 * Contact picker options, shaped as Create Customer Order's customer picker:
 * the account number as the badge, "Name (address line 1)", searched by # or
 * name. `extra` keeps a contact already named in the list even when it is not
 * an active contact any more.
 */
/** A contact's first address line for a picker: delivery address, else its address, else invoice. */
export function contactLine1(c) {
  if (!c) return '';
  return String(c.delivery_line1 || '').trim()
    || addressLine1(c.address)
    || String(c.invoice_line1 || '').trim();
}

export function deliverToOptions(contacts, extra = []) {
  const seen = new Set();
  const out = [];
  for (const c of contacts || []) {
    const line = contactLine1(c);
    seen.add(String(c.id));
    out.push({
      value: String(c.id),
      badge: c.account_no || ' ',
      label: c.company_name + (line ? ` (${line})` : ''),
      searchText: `${c.account_no || ''} ${c.company_name} ${line}`
    });
  }
  for (const e of extra) {
    if (!e || e.accountId == null || seen.has(String(e.accountId))) continue;
    seen.add(String(e.accountId));
    out.push({ value: String(e.accountId), badge: ' ', label: e.name || 'This contact', searchText: e.name || '' });
  }
  return out;
}

/** The value prefix of a "Collect from" that is no contact (typed before, or the order's own words). */
const TEXT_PREFIX = 'text:';

/** The contact a collection point's name is, matched on the name (case and spaces ignored). */
export function contactForName(contacts, name) {
  const k = String(name || '').trim().toLowerCase();
  if (!k) return null;
  return (contacts || []).find(c => String(c.company_name || '').trim().toLowerCase() === k) || null;
}

/**
 * The Collect from options for one point: every active contact, plus the
 * point's current name and the order's when they are no contact, so what the
 * PO-H says now is always shown.
 */
export function collectFromOptions(contacts, names = []) {
  const out = deliverToOptions(contacts);
  const seen = new Set();
  for (const n of names) {
    const name = String(n || '').trim();
    if (!name || contactForName(contacts, name) || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    out.push({ value: TEXT_PREFIX + name, badge: ' ', label: name, searchText: name });
  }
  return out;
}

/** The picker's value for a point's name. */
export function collectFromValue(contacts, name) {
  const c = contactForName(contacts, name);
  if (c) return String(c.id);
  return String(name || '').trim() ? TEXT_PREFIX + String(name).trim() : '';
}

function HaulagePoDetailsModal({ poId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/purchase-orders/${encodeURIComponent(poId)}/haulage-details`, { credentials: 'include' });
        const body = await res.json();
        if (!res.ok || !body.success) throw new Error(body.error || 'The PO-H could not be read');
        if (gone) return;
        setData(body);
        setForm({
          points: body.points.map(p => ({ key: p.key, from: p.from, address: p.address })),
          haulierNotes: body.haulierNotes || '',
          internalNotes: body.internalNotes || ''
        });
      } catch (err) {
        if (!gone) setError(err.message);
      }
    })();
    // Every active contact of any type — suppliers and customers alike (the
    // haulier may collect from either); archived ones are left out.
    fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&limit=10000`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!gone) setContacts(d.contacts || []); })
      .catch(() => { if (!gone) setContacts([]); });
    return () => { gone = true; };
  }, [poId]);

  const block = data ? (data.ready ? data.block : data.reason) : null;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // The delivery is the order's (Cam, 19 Oct): it is never sent from here.
      const payload = { collection: form.points, haulierNotes: form.haulierNotes, internalNotes: form.internalNotes };
      const res = await fetch(`${API_URL}/api/purchase-orders/${encodeURIComponent(poId)}/haulage-details`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(payload)
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Not saved');
      onSaved(body);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const footer = (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={onClose}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Cancel</span>
        </button>
      </div>
      <div>
        <button type="button" className="btn-slideout" onClick={save} disabled={!form || saving || !!block} title={block || ''}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
          <span className="btn-label">{saving ? 'Saving…' : 'Save'}</span>
        </button>
      </div>
    </div>
  );

  return (
    <Modal isOpen onClose={onClose} title={`Edit PO-H${data ? ` — ${data.po.number}` : ''}`} size="lg" className="po-h-details" footer={footer}>
      <DetailsBody data={data} form={form} setForm={setForm} error={error} block={block}
        contacts={contacts} saving={saving} />
    </Modal>
  );
}

/** The window's body; mounts with the footer, so it measures the slide-out buttons. */
function DetailsBody({ data, form, setForm, error, block, contacts, saving }) {
  useSlideOutButton([!!form, saving]);
  const names = useMemo(() => (data && form)
    ? data.points.flatMap((p, i) => [form.points[i]?.from, p.computedFrom])
    : [], [data, form]);
  const options = useMemo(() => collectFromOptions(contacts || [], names), [contacts, names]);
  if (!data && !error) return <LoadingSpinner />;
  const setPoint = (i, patch) => setForm(prev => ({ ...prev, points: prev.points.map((p, j) => (j === i ? { ...p, ...patch } : p)) }));
  const sourceLabel = data?.delivery?.source?.label || 'its order';
  // A stock / self-billed PO's PO-H follows its PO, not an order (2026-10-29).
  const srcKind = data?.delivery?.source?.kind;
  const srcWord = srcKind === 'stock' || srcKind === 'self_billed' ? 'PO' : 'order';
  const pickCollectFrom = (i, value) => {
    if (!value) return;
    if (String(value).startsWith(TEXT_PREFIX)) {
      const name = String(value).slice(TEXT_PREFIX.length);
      const p = data.points[i];
      // The order's own words: back to its address too.
      if (p && name === p.computedFrom) setPoint(i, { from: name, address: p.computedAddress });
      else setPoint(i, { from: name });
      return;
    }
    const c = (contacts || []).find(x => String(x.id) === String(value));
    if (!c) return;
    setPoint(i, { from: c.company_name, address: contactDeliveryAddress(c) || form.points[i].address });
  };
  return (
    <>
      {error && <p className="po-h-note err"><span className="material-symbols-rounded">error</span>{error}</p>}
      {block && <p className="po-h-note"><span className="material-symbols-rounded">info</span>{block}</p>}
      {data && form && (
        <>
          <p className="po-h-muted">
            Its lines, haulier, delivery date, service and delivery address follow {sourceLabel} — change those there.
            The collection and the notes set here are this PO-H&apos;s own.
            {data.po.delivered ? ' The order is delivered, so this is a correction of our records: the haulier is not sent a revised PO-H.' : ''}
          </p>

          <h4 className="po-h-heading">Collection</h4>
          <p className="po-h-muted">
            Where {data.po.haulier || 'the haulier'} collects from — any supplier or customer. Taken from the {srcWord} unless changed
            here; a change is kept when the {srcWord} is saved again, as long as the line is still collected from the same supplier.
          </p>
          {data.points.length === 0 && <p className="po-h-muted">This PO-H has no lines.</p>}
          {data.points.map((p, i) => (
            <div key={p.key} className="po-h-point">
              <div className="po-h-point-head">
                <span className="material-symbols-rounded">warehouse</span>
                <b>{p.computedFrom || 'Collection'}</b>
                <span className="po-h-muted">{p.lines} line{p.lines === 1 ? '' : 's'}</span>
                <button type="button" className="po-h-link" disabled={!!block}
                  onClick={() => setPoint(i, { from: p.computedFrom, address: p.computedAddress })}
                  title={`Put back what the ${srcWord} says`}>Use the {srcWord}&apos;s</button>
              </div>
              <label>Collect from</label>
              <div className="po-h-deliver-to">
                <SearchableDropdown
                  value={collectFromValue(contacts, form.points[i].from)}
                  onChange={(v) => pickCollectFrom(i, v)}
                  options={options}
                  placeholder={contacts === null ? 'Loading contacts…' : 'Select a supplier or customer'}
                  searchPlaceholder="Search by # or name..."
                  disabled={!!block || contacts === null}
                />
              </div>
              <label>Collection address
                <Textarea rows={4} value={form.points[i].address} disabled={!!block} maxLength={ADDRESS_MAX}
                  onChange={(e) => setPoint(i, { address: e.target.value })} placeholder={p.computedAddress || `No address on the ${srcWord}`} />
              </label>
              <p className="po-h-muted">Filled from the contact&apos;s delivery address (else its invoice address) — change it here if the goods are collected from somewhere else.</p>
              {p.computedAddress && trimmed(form.points[i].address) !== trimmed(p.computedAddress) && (
                <p className="po-h-muted">The {srcWord} says: {p.computedFrom ? `${p.computedFrom}, ` : ''}{p.computedAddress.replace(/\n+/g, ', ')}</p>
              )}
            </div>
          ))}
          <h4 className="po-h-heading">Notes for the haulier</h4>
          <p className="po-h-muted">Printed on the PO-H and in what is sent to the haulier.</p>
          <Textarea rows={3} value={form.haulierNotes} disabled={!!block} maxLength={NOTE_MAX}
            onChange={(e) => setForm(prev => ({ ...prev, haulierNotes: e.target.value }))} />
          <h4 className="po-h-heading">Internal notes</h4>
          <p className="po-h-muted">Ours only — never printed or sent.</p>
          <Textarea rows={3} value={form.internalNotes} disabled={!!block} maxLength={NOTE_MAX}
            onChange={(e) => setForm(prev => ({ ...prev, internalNotes: e.target.value }))} />
        </>
      )}
    </>
  );
}

export default HaulagePoDetailsModal;
